import React from "react";
import { ArrowDown, ArrowUp, ChevronDown, ChevronRight, Copy, Plus, Trash2, X } from "lucide-react";
import { ActionMenu, ActionMenuDivider, ActionMenuItem, AppButton, AppCard, IconButton, Input } from "../ui";

/**
 * One day of a day-by-day syllabus — one class: its title, length and topics.
 * Enter in a topic adds the next one.
 *
 * Props:
 *   day, index, count, open, onToggle, errors, focusUid
 *   onChange(patch), onMove(delta), onRemove(), onDuplicate()
 *   onTopicChange(uid, title), onTopicAddAfter(uid), onTopicRemove(uid)
 */
const DayCard = ({
  day, index, count, open, onToggle, errors = {}, focusUid,
  onChange, onMove, onRemove, onDuplicate,
  onTopicChange, onTopicAddAfter, onTopicRemove,
}) => {
  const name = day.title.trim() || `Day ${index + 1}`;
  const topicCount = day.topics.filter((t) => t.title.trim()).length;
  const error = errors[day.uid];
  const topicsError = errors[`${day.uid}:topics`];

  return (
    <AppCard as="section" aria-label={`Day ${index + 1}: ${name}`} overflowVisible>
      <div className="tw-flex tw-flex-col tw-gap-3 tw-p-4">
        <div className="tw-flex tw-items-start tw-gap-2">
          <IconButton
            size="sm"
            icon={open ? ChevronDown : ChevronRight}
            label={open ? `Collapse ${name}` : `Expand ${name}`}
            aria-expanded={open}
            onClick={onToggle}
            className="tw-mt-1.5"
          />
          <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-1">
            <div className="tw-grid tw-items-center tw-gap-2 min-[576px]:tw-grid-cols-[auto_minmax(0,1fr)_7.5rem]">
              <span className="tw-w-fit tw-rounded-sm tw-bg-primary/10 tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold tw-text-primary">
                Day {index + 1}
              </span>
              <Input
                size="lg"
                value={day.title}
                invalid={!!error}
                onChange={(e) => onChange({ title: e.target.value })}
                placeholder="What this day covers — e.g. Java basics"
                aria-label={`Day ${index + 1} title`}
                maxLength={200}
                autoFocus={focusUid === day.uid}
                className="tw-font-semibold"
              />
              <span className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-content-muted">
                <Input
                  type="number"
                  min={5}
                  max={600}
                  step={5}
                  className="tw-min-w-0 tw-flex-1 tw-tabular-nums"
                  value={day.minutes}
                  onChange={(e) => onChange({ minutes: e.target.value })}
                  aria-label={`Minutes for day ${index + 1}`}
                />
                min
              </span>
            </div>
            {error && <p role="alert" className="tw-m-0 tw-text-xs tw-text-danger">{error}</p>}
            {!open && <p className="tw-m-0 tw-text-xs tw-text-content-muted">{topicCount} topic{topicCount === 1 ? "" : "s"}</p>}
          </div>
          <ActionMenu label={`Actions for ${name}`}>
            <ActionMenuItem onClick={onDuplicate}><Copy size={14} aria-hidden="true" /> Duplicate day</ActionMenuItem>
            <ActionMenuItem onClick={() => onMove(-1)} disabled={index === 0}><ArrowUp size={14} aria-hidden="true" /> Move day up</ActionMenuItem>
            <ActionMenuItem onClick={() => onMove(1)} disabled={index === count - 1}><ArrowDown size={14} aria-hidden="true" /> Move day down</ActionMenuItem>
            <ActionMenuDivider />
            <ActionMenuItem danger onClick={onRemove}><Trash2 size={14} aria-hidden="true" /> Delete day</ActionMenuItem>
          </ActionMenu>
        </div>

        {open && (
          <div className="tw-flex tw-flex-col tw-gap-2 tw-ps-1 min-[576px]:tw-ps-10">
            {topicsError && <p role="alert" className="tw-m-0 tw-text-xs tw-text-danger">{topicsError}</p>}
            <ol className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-1.5 tw-p-0">
              {day.topics.map((t, i) => (
                <li key={t.uid} className="tw-flex tw-items-center tw-gap-2">
                  <span className="tw-w-6 tw-shrink-0 tw-text-right tw-text-xs tw-font-semibold tw-text-content-subtle">{i + 1}.</span>
                  <Input
                    value={t.title}
                    onChange={(e) => onTopicChange(t.uid, e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        onTopicAddAfter(t.uid);
                      }
                    }}
                    placeholder="A topic covered this day"
                    aria-label={`Day ${index + 1}, topic ${i + 1}`}
                    maxLength={200}
                    autoFocus={focusUid === t.uid}
                  />
                  <IconButton
                    size="sm"
                    icon={X}
                    label={`Remove topic ${i + 1} of day ${index + 1}`}
                    onClick={() => onTopicRemove(t.uid)}
                    disabled={day.topics.length <= 1}
                  />
                </li>
              ))}
            </ol>
            <div>
              <AppButton size="sm" variant="ghost" onClick={() => onTopicAddAfter(day.topics[day.topics.length - 1]?.uid)}>
                <Plus size={14} aria-hidden="true" /> Add topic
              </AppButton>
            </div>
          </div>
        )}
      </div>
    </AppCard>
  );
};

export default DayCard;
